/**
 * TalarMedical Dashboard Application - Main App Component
 * 
 * Secure authentication-enabled React dashboard for Talar Medical business intelligence
 * Features: Frontend authentication, real data integration, professional transitions
 * 
 * Created for: TalarMedical-A2B-DataProject-MVP
 * Phase: 2 - Authentication System Implementation
 * Powered by: A2B Business Consulting LLC 2026
 */

import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import Login from './components/Login';

/**
 * Dashboard Data Interface
 * 
 * TypeScript interface defining the complete data structure for TalarMedical dashboard
 * Includes vendor analytics, financial metrics, forecasting, and transaction data
 * Used for type safety across authentication and dashboard components
 */
interface DashboardData {
  summary: {
    totalRevenue: number;
    totalAdminFees: number;
    totalCommissions: number;
    totalTalarRevenue: number;
    totalVendors: number;
    activeVendors: number;
    totalCustomers: number;
    revenueCustomers: number;
    totalTransactions: number;
    revenueTransactions: number;
  };
  vendors: Array<{
    name: string;
    revenue: number;
    admin_fees: number;
    commissions: number;
    talar_revenue: number;
    transactions: number;
    revenue_transactions: number;
    growth: number;
    customers: number;
    status: string;
  }>;
  monthlyRevenue: Array<{
    month: string;
    revenue: number;
    admin_fees: number;
    commissions: number;
    talar_revenue: number;
    transactions: number;
  }>;
  vendorMonthlyRevenue: Record<string, Array<{
    month: string;
    revenue: number;
    admin_fees: number;
    commissions: number;
    talar_revenue: number;
    transactions: number;
  }>>;
  topCustomers: Array<{
    name: string;
    revenue: number;
    transactions: number;
  }>;
  vendorTopCustomers: Record<string, Array<{
    name: string;
    revenue: number;
    transactions: number;
  }>>;
  recentTransactions: Array<{
    id: string;
    vendor: string;
    customer: string;
    amount: number;
    admin_fee: number;
    commission: number;
    date: string;
    product: string;
    status: string;
  }>;
  vendorTransactions: Record<string, Array<{
    id: string;
    vendor: string;
    customer: string;
    amount: number;
    admin_fee: number;
    commission: number;
    date: string;
    product: string;
    status: string;
  }>>;
  products: Array<{
    name: string;
    vendor: string;
    gross_sales: number;
    talar_revenue: number;
    margin_pct: number;
    quantity: number;
    transactions: number;
  }>;
  forecast: {
    monthly: Array<{
      month: string;
      month_short: string;
      revenue: number;
      talar: number;
      revenue_low: number;
      revenue_high: number;
      talar_low: number;
      talar_high: number;
    }>;
    quarterly: Array<{
      quarter: string;
      revenue: number;
      talar: number;
      revenue_low: number;
      revenue_high: number;
      talar_low: number;
      talar_high: number;
    }>;
    annual: {
      revenue_2025_actual: number;
      revenue_2026_base: number;
      revenue_2026_low: number;
      revenue_2026_high: number;
      talar_2025_actual: number;
      talar_2026_base: number;
      talar_2026_low: number;
      talar_2026_high: number;
      revenue_growth_pct: number;
      talar_growth_pct: number;
    };
    byVendor: Array<{
      vendor: string;
      revenue_2025: number;
      revenue_2026: number;
      talar_2025: number;
      talar_2026: number;
      growth_pct: number;
    }>;
    actuals2025: Array<{
      month: string;
      revenue: number | null;
      talar: number | null;
    }>;
    methodology: string;
    dataMonthsCount: number;
  };
}

/**
 * Main App Component
 * 
 * Controls authentication flow, data loading, and component rendering
 * Manages secure transitions between login and dashboard states
 * Integrates with TalarMedical real data and A2B authentication system
 */
function App() {
  // Dashboard data state - stores complete TalarMedical business intelligence data
  const [data, setData] = useState<DashboardData | null>(null);
  
  // Loading state - controls professional loading transitions during auth and data fetch
  const [loading, setLoading] = useState(true);
  
  // Authentication state - determines if user has successfully logged in
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // User information - stores authenticated user details from credentials.json
  const [userInfo, setUserInfo] = useState<any>(null);

  /**
   * Authentication Handler
   * 
   * Processes login results from Login component
   * Updates authentication state and stores user information for dashboard display
   * Provides console logging for debugging and monitoring authentication flow
   */
  const handleLogin = (authenticated: boolean, user?: any) => {
    setIsAuthenticated(authenticated);
    setUserInfo(user);
    
    if (authenticated) {
      console.log('✅ User authenticated:', user.name, '(' + user.role + ')');
    }
  };

  /**
   * Logout Handler
   * 
   * Manages secure logout process with professional loading transition
   * Clears all authentication state, user data, and dashboard data
   * Provides 1.5-second loading screen for smooth user experience
   */
  const handleLogout = () => {
    console.log('🚪 User logging out:', userInfo?.name);
    setLoading(true);
    
    // Simulate logout processing time with professional transition (1.5 seconds)
    setTimeout(() => {
      setIsAuthenticated(false);  // Clear authentication status
      setUserInfo(null);          // Clear user information
      setData(null);              // Clear dashboard data for security
      setLoading(false);          // Show login screen
      console.log('✅ Logout completed successfully');
    }, 1500);
  };

  /**
   * Data Loading Effect
   * 
   * Loads TalarMedical business intelligence data after successful authentication
   * Fetches real-data.json from MVP-Data-Folder processing with sample-data fallback
   * Provides comprehensive error handling and console logging for monitoring
   */
  useEffect(() => {
    // Skip data loading if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Load real TalarMedical data from MVP-Data-Folder processing pipeline
    fetch('/real-data.json')
      .then(response => response.json())
      .then((data: DashboardData) => {
        console.log('✅ Loaded real TalarMedical data:', {
          revenue: `$${data.summary.totalRevenue.toLocaleString()}`,
          activeVendors: `${data.summary.activeVendors}/${data.summary.totalVendors}`,
          revenueCustomers: `${data.summary.revenueCustomers}/${data.summary.totalCustomers}`,
          revenueTransactions: `${data.summary.revenueTransactions}/${data.summary.totalTransactions}`
        });
        setData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('❌ Error loading real data, falling back to sample data:', error);
        // Graceful fallback to sample data if real data processing fails
        return fetch('/sample-data.json');
      })
      .then(response => response?.json())
      .then((data: DashboardData) => {
        if (data) {
          console.log('📋 Loaded fallback sample data');
          setData(data);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('❌ Error loading any data:', error);
        setLoading(false);
      });
  }, [isAuthenticated]);  // Re-run when authentication status changes

  // AUTHENTICATION GUARD: Show A2B Business Consulting login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="App">
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  // LOADING STATE: Show professional loading screen while fetching data or during logout
  if (loading) {
    const isLoggingOut = isAuthenticated && !data;
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{isLoggingOut ? 'Signing out...' : 'Loading TalarMedical Dashboard...'}</p>
      </div>
    );
  }

  // ERROR STATE: Show error message if TalarMedical data failed to load
  if (!data) {
    return (
      <div className="error-container">
        <p>Error loading dashboard data</p>
      </div>
    );
  }

  // DASHBOARD STATE: Show complete TalarMedical dashboard with user info and logout functionality
  return (
    <div className="App">
      <Dashboard data={data} userInfo={userInfo} onLogout={handleLogout} />
    </div>
  );
}

export default App;